import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { RichTextField } from '@/components/form/fields/text/RichTextInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { PurchaseOrderDeliveryOption } from '@/services/api/contracts/catalog/bundles';
import { useAddress } from '@/services/api/hooks/address';
import { useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { useContactDetail } from '@/services/api/hooks/contact';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { useCatalogueRoutePrefix } from '@/services/catalogue/useCatalogueRoutePrefix';
import {
  usePoContact,
  usePoDelivery,
  usePoDetails,
  usePoName,
  usePoSelectedDeviceFee,
  usePoSelectedMainDevices,
  useSetPoDetails
} from '@/services/purchaseOrders/usePurchaseOrderService';
import { usePONavigateAndValidate } from '@/services/purchaseOrders/validation/purchaseOrderHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { Value } from '@udecode/plate-common';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod';
import PurchaseOrderTotalCostSummary from '../PurchaseOrderTotalCostSummary';
import SavePurchaseOrderButton from '../SavePurchaseOrder';
import { ConfirmPurchaseOrderModal } from './ConfirmPurchaseOrderModal';

interface PurchaseOrderConfirmOrderDetailsProps {}

const REF_ERROR_MESSAGE = 'PO Reference or Stocking Order # must be filled in';
const NAME_ERROR_MESSAGE = 'Order / quote must have a name';

const OrderDetailFields = z
  .object({
    name: z.string(),
    poRef: z.string(),
    stockingOrder: z.string(),
    partnerRef: z.string(),
    notes: z.custom<Value>()
  })
  .partial()
  .refine(data => data.name, NAME_ERROR_MESSAGE)
  .refine(data => data.poRef || data.stockingOrder, REF_ERROR_MESSAGE);

type OrderDetailFields = z.infer<typeof OrderDetailFields>;

const PurchaseOrderConfirmOrderDetails: FunctionComponent<PurchaseOrderConfirmOrderDetailsProps> = () => {
  const {
    poRef: savedPoRef,
    stockingOrder: savedStockingOrder,
    partnerRef: savedPartnerRef,
    notes: savedNotes
  } = usePoDetails();
  const orderName = usePoName();

  const methods = useForm<OrderDetailFields>({
    resolver: zodResolver(OrderDetailFields),
    defaultValues: {
      name: orderName || '',
      poRef: savedPoRef,
      stockingOrder: savedStockingOrder,
      partnerRef: savedPartnerRef,
      notes: savedNotes?.value
    }
  });

  const deliveryDetails = usePoDelivery();
  const { partnerId, companyId, orderId } = useParams();
  const [address] = useAddress(partnerId, companyId, deliveryDetails?.addressId);

  if (!orderId) throw new Error('Missing order id');

  const [hasError, setHasError] = useState(false);

  const modalProps = useModal();

  const selectedDeviceFee = usePoSelectedDeviceFee();
  const [showDeviceFeeMandatoryMessage, setShowDeviceFeeMandatoryMessage] = useState(false);

  const mainDevices = usePoSelectedMainDevices();

  useEffect(() => {
    if (selectedDeviceFee) {
      setShowDeviceFeeMandatoryMessage(false);
    }
  }, [selectedDeviceFee]);

  const onConfirm = async () => {
    if (mainDevices.length > 0 && !selectedDeviceFee) {
      setShowDeviceFeeMandatoryMessage(true);
      return;
    }

    const isValid = await methods.trigger();

    if (!isValid) {
      setHasError(!isValid);
    } else {
      modalProps.open();
    }
  };

  const { setPoRef, setPartnerRef, setStockingOrder, setNotes } = useSetPoDetails();

  const poRef = methods.watch('poRef');
  useEffect(() => {
    setPoRef(poRef);
  }, [poRef, setPoRef]);

  const partnerRef = methods.watch('partnerRef');
  useEffect(() => {
    setPartnerRef(partnerRef);
  }, [partnerRef, setPartnerRef]);

  const stockingOrder = methods.watch('stockingOrder');
  useEffect(() => {
    setStockingOrder(stockingOrder);
  }, [stockingOrder, setStockingOrder]);

  const nameEntered = methods.watch('name');
  useEffect(() => {
    setStockingOrder(stockingOrder);
  }, [nameEntered, setStockingOrder]);

  const notes = methods.watch('notes');
  useEffect(() => {
    if (notes) {
      setNotes({ value: notes });
    } else {
      setNotes();
    }
  }, [notes, setNotes]);

  const role = useRole();

  // TODO  once approval functionality in place
  const customerNeedsToApprove = false;

  const confirmButtonText =
    role.type === RoleType.Company ? (customerNeedsToApprove ? 'Approve Order' : 'Confirm Order') : 'Place Order';

  if (!(partnerId && companyId)) throw new Error('Missing params');

  const [mspDetails] = useMspDetailsForCustomer(partnerId, companyId);

  const routePrefix = useCatalogueRoutePrefix();
  const navigateAndValidate = usePONavigateAndValidate();

  const contactDetails = usePoContact();
  const [contact] = useContactDetail(contactDetails?.contactId);

  return (
    <div className="space-y-3">
      <p className="text-2xl font-bold">Details</p>

      <Form className="flex w-full flex-col space-y-4" methods={methods} onSubmit={() => {}}>
        <div className="w-full space-y-2">
          {hasError && (
            <p className="relative mt-0.5 w-full rounded-md border-red-100 bg-red-50 px-1 py-2 text-center text-sm font-medium text-red-500">
              {nameEntered ? REF_ERROR_MESSAGE : NAME_ERROR_MESSAGE}
            </p>
          )}
          <TextField id="name" label="Quote / Order Name" />

          <Card className="py-3 px-4 shadow-sm border-gray-400">
            <p className="mb-1">For orders only</p>
            <div className="flex flex-row space-x-10">
              <div className="max-w-[550px]">
                <div className="flex flex-row items-center space-x-10">
                  <TextField className="flex-1" id="poRef" label="PO Reference" />
                  {role.type === RoleType.Admin && (
                    <>
                      <p className="font-semibold pt-7">OR</p>
                      <TextField className="flex-1" id="stockingOrder" label="DaaS Portal Reference" />
                    </>
                  )}
                </div>
                <TextField id="partnerRef" label="Partner Reference" />
              </div>
              <div className="w-full">
                <RichTextField
                  containerClassName="border-primary-std h-fit w-full rounded-t-lg border bg-white"
                  id="notes"
                  label="Notes / Special Instructions"
                />
              </div>
            </div>
          </Card>
        </div>
      </Form>

      {!deliveryDetails ? (
        <div className="space-y-5">
          <div>No delivery details have been provided.</div>
          <Button
            onClick={() => navigateAndValidate(`${routePrefix}/poCatalogue/deliveryDetails`)}
            variant={ButtonVariant.Secondary}
          >
            Add Delivery Details
          </Button>
        </div>
      ) : (
        <div className="text-sm space-y-1">
          <div>
            <p className="font-semibold">Deliver To:</p>
            {deliveryDetails?.type === PurchaseOrderDeliveryOption.Daas && <p>DaaS Stock</p>}
            {deliveryDetails?.type === PurchaseOrderDeliveryOption.Msp && <p>{mspDetails.name} Stock</p>}
            {deliveryDetails?.type === PurchaseOrderDeliveryOption.Custom && (
              <div>
                <p>{address?.name}</p>
                <p>
                  {address?.line1} {address?.line2 ? `, ${address?.line2}` : ''}
                </p>
                <p>{address?.city}</p>
                <p>{address?.state}</p>
                <p>{address?.postcode}</p>
                <p>{address?.country}</p>
              </div>
            )}
          </div>

          {deliveryDetails?.type === PurchaseOrderDeliveryOption.Custom && contact !== null && (
            <div>
              <p className="font-semibold">Contact:</p>
              <p>Work email: {contact?.workEmail}</p>
              {contact?.personalEmail && <p>Personal email: {contact.personalEmail}</p>}
              <p>Telephone number: {contact?.telephoneNumber}</p>
            </div>
          )}
        </div>
      )}

      <div className="text-xl font-semibold">
        <p>Summary</p>
        <PurchaseOrderTotalCostSummary />
      </div>
      {showDeviceFeeMandatoryMessage && <p className="text-red-500 text-md font-medium">Please select a device fee.</p>}
      <div className="flex space-x-2">
        <SavePurchaseOrderButton customText="Save as Draft" overrideName={nameEntered} />
        <Button variant={ButtonVariant.Primary} onClick={onConfirm}>
          {confirmButtonText}
        </Button>
      </div>
      <ConfirmPurchaseOrderModal {...modalProps} nameToSave={nameEntered!} />
    </div>
  );
};

export default PurchaseOrderConfirmOrderDetails;
